// File: frontend/src/components/admin/tabs/SimpleTabs.js
//
// The smaller tabs: activity feed, churn, devices, geography, errors and the
// custom range. Grouped in one file because each is a screenful of markup over
// one endpoint, and a file apiece would be more ceremony than content.
//
// Every one of these used literal Tailwind shades before — `bg-green-50` rows,
// `bg-red-50` error cards, `text-red-700` bodies. On the dark theme those held
// their light-mode values, so the Activity and Errors tabs were pale blocks on
// charcoal with cream text sitting on top of them.
import {
  Activity, RefreshCw, Trash2, TrendingUp, Clock, Target, Monitor, Smartphone,
  Globe, MapPin, Shield, AlertTriangle, UserPlus, Users, CheckCircle, Calendar,
} from 'lucide-react';
import {
  Card, StatCard, StatGrid, SectionError, EmptyState, Loading, IconButton,
  SeverityBadge, ScrollRegion,
} from '../primitives';
import { BarChart, RankedBars, CHART_TOKENS } from '../charts';
import { formatInstant, formatRelative, formatCalendarDate } from '../../../utils/adminDates';

// ── Activity ────────────────────────────────────────────────────────────────
const ACTION_TONE = {
  signup: { tone: 'success', icon: UserPlus },
  login: { tone: 'info', icon: Users },
  account_deleted: { tone: 'danger', icon: Trash2 },
};
const actionMeta = (action) => ACTION_TONE[action] || { tone: 'accent', icon: Activity };

const TONE_ICON = {
  success: 'text-success', info: 'text-info', danger: 'text-danger', accent: 'text-admin',
};

export function ActivityTab({ data, error, loading, onRetry, onOpenUser }) {
  const activities = data?.activities || [];
  return (
    <Card
      title="Activity feed"
      icon={Activity}
      tone="accent"
      action={<IconButton icon={RefreshCw} label="Refresh activity" onClick={onRetry} />}
    >
      <SectionError message={error} onRetry={onRetry} />
      {loading && activities.length === 0 && <Loading />}
      {activities.length === 0 && !loading && !error && <EmptyState>No activity yet</EmptyState>}
      <ScrollRegion className="space-y-2" desktop="sm:max-h-[32rem]">
        {activities.map((a, i) => {
          const { tone, icon: Icon } = actionMeta(a.action);
          // The row is a surface, not a tinted block: colour lives in the icon
          // so a long feed does not become a stack of coloured slabs.
          const clickable = a.user_id != null && onOpenUser;
          const Row = clickable ? 'button' : 'div';
          return (
            <Row
              key={a.id || i}
              {...(clickable ? { type: 'button', onClick: () => onOpenUser(a.user_id) } : {})}
              className={`w-full text-left p-3 rounded-xl border border-divider bg-surface-input flex items-start gap-3 ${
                clickable ? 'hover:bg-[var(--color-hover-surface)] cursor-pointer' : ''
              }`}
            >
              <Icon size={16} className={`${TONE_ICON[tone]} mt-0.5 shrink-0`} aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-medium text-sm text-ink-main capitalize">{a.action.replace(/_/g, ' ')}</span>
                  {a.email && <span className="text-xs text-ink-light truncate">{a.email}</span>}
                </div>
                <div className="text-xs text-ink-light flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                  <span title={formatInstant(a.created_at)}>{formatRelative(a.created_at)}</span>
                  {a.country && a.country !== 'Unknown' && (
                    <span className="flex items-center gap-1"><MapPin size={10} />{a.city}, {a.country}</span>
                  )}
                  {a.platform && (
                    <span className="px-1.5 py-0.5 rounded bg-surface-card text-[10px] text-ink-light">{a.platform}</span>
                  )}
                </div>
              </div>
            </Row>
          );
        })}
      </ScrollRegion>
    </Card>
  );
}

// ── Churn ───────────────────────────────────────────────────────────────────
export function ChurnTab({ data, error, loading, onRetry }) {
  if (error) return <SectionError message={error} onRetry={onRetry} />;
  if (!data) return loading ? <Loading>Loading churn…</Loading> : null;
  const { stats, churnByDay, deletedUsers } = data;

  return (
    <div className="space-y-4 sm:space-y-6">
      <StatGrid cols={4}>
        <StatCard title="Deleted all time" value={stats.totalDeleted} icon={Trash2} tone="danger" />
        <StatCard title="Deleted (30 days)" value={stats.deletedInPeriod} icon={TrendingUp} tone="warning" />
        <StatCard title="Avg days active" value={stats.avgDaysActive} subtitle="Signup to last sign of life" icon={Clock} tone="info" />
        <StatCard
          title="Avg completions"
          value={stats.avgCompletionsBeforeDeletion}
          subtitle={`${stats.avgHabitsBeforeDeletion} habits on average`}
          icon={Target}
          tone="accent"
        />
      </StatGrid>

      <Card title="Deletions over time" icon={Trash2} tone="danger">
        <BarChart data={churnByDay} token={CHART_TOKENS[4]} height={140} label="Account deletions per day" />
      </Card>

      <Card title="Recently deleted" icon={Trash2} tone="neutral">
        {deletedUsers.length === 0 ? <EmptyState>No deletions in this period</EmptyState> : (
          <ScrollRegion className="space-y-2" desktop="sm:max-h-80">
            {deletedUsers.map((u, i) => (
              <div key={i} className="flex flex-wrap justify-between items-baseline gap-2 p-3 bg-surface-input rounded-xl text-sm">
                <span className="font-mono text-ink-main">User #{u.original_user_id}</span>
                <div className="text-right text-ink-light text-xs">
                  <div className="admin-nums">{u.days_active} days active · {u.total_completions} completions</div>
                  <div title={formatInstant(u.deletion_date)}>{formatRelative(u.deletion_date)}</div>
                </div>
              </div>
            ))}
          </ScrollRegion>
        )}
        <p className="text-xs text-ink-subtle mt-3 leading-snug">
          Anonymous snapshots — no email or personal data is kept after deletion. Rows recorded
          before the last-active fix may show a span measured from the wrong end; their source
          data is gone and cannot be recomputed.
        </p>
      </Card>
    </div>
  );
}

// ── Devices ─────────────────────────────────────────────────────────────────
export function DevicesTab({ data, error, loading, onRetry }) {
  if (error) return <SectionError message={error} onRetry={onRetry} />;
  if (!data) return loading ? <Loading>Loading devices…</Loading> : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
      <Card title="Device type" icon={Monitor} tone="info">
        <RankedBars
          rows={data.deviceBreakdown.map(d => ({ label: d.device_type, value: Number(d.count), suffix: 'events' }))}
          token={CHART_TOKENS[1]}
        />
      </Card>
      <Card title="Platform" icon={Smartphone} tone="success">
        <RankedBars
          rows={data.platformBreakdown.map(p => ({ label: p.platform, value: Number(p.count), suffix: 'events' }))}
          token={CHART_TOKENS[2]}
        />
      </Card>
      {/* Both of these were returned by the endpoint and never rendered. Unique
          people matter more than raw event counts, which any one heavy user
          can dominate. */}
      <Card title="People by device type" icon={Monitor} tone="accent">
        <RankedBars
          rows={(data.uniqueByDevice || []).map(d => ({ label: d.device_type, value: Number(d.unique_users), suffix: 'users' }))}
          token={CHART_TOKENS[0]}
        />
      </Card>
      <Card title="People by platform" icon={Smartphone} tone="warning">
        <RankedBars
          rows={(data.uniqueByPlatform || []).map(p => ({ label: p.platform, value: Number(p.unique_users), suffix: 'users' }))}
          token={CHART_TOKENS[3]}
        />
      </Card>
      <div className="lg:col-span-2">
        <p className="text-xs text-ink-subtle leading-snug">
          Derived from user-agent strings on logged requests, so this is &ldquo;what browsers we saw&rdquo;
          rather than a device registry. A user on both a phone and a laptop appears in each.
        </p>
      </div>
    </div>
  );
}

// ── Geography ───────────────────────────────────────────────────────────────
export function GeoTab({ data, error, loading, onRetry }) {
  if (error) return <SectionError message={error} onRetry={onRetry} />;
  if (!data) return loading ? <Loading>Loading geography…</Loading> : null;

  return (
    <div className="space-y-4 sm:space-y-6">
      <Card title="Users by country" icon={Globe} tone="info">
        <RankedBars
          rows={data.countryBreakdown.slice(0, 12).map(c => ({ label: c.country, value: Number(c.unique_users), suffix: 'users' }))}
          token={CHART_TOKENS[1]}
        />
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <Card title="Top cities" icon={MapPin} tone="accent">
          <RankedBars
            rows={data.cityBreakdown.slice(0, 10).map(c => ({ label: `${c.city}, ${c.country}`, value: Number(c.unique_users), suffix: 'users' }))}
            token={CHART_TOKENS[0]}
          />
        </Card>
        {/* Returned by the endpoint, never drawn. */}
        <Card title="Where signups come from" icon={UserPlus} tone="success">
          <RankedBars
            rows={(data.signupsByCountry || []).slice(0, 10).map(c => ({ label: c.country, value: Number(c.count) }))}
            token={CHART_TOKENS[2]}
            emptyLabel="No located signups in this period"
          />
        </Card>
      </div>
    </div>
  );
}

// ── Errors & security ───────────────────────────────────────────────────────
const KIND_LABELS = {
  auth_failure: 'Failed login',
  invalid_token: 'Invalid token',
  admin_probe: 'Admin probe',
  rate_limited: 'Rate limited',
  not_found: '404 probe',
  forbidden: 'Forbidden',
  bad_request: 'Bad request',
  cors_blocked: 'Blocked origin',
};

function SecurityEventRow({ event }) {
  const sameTime = event.first_seen === event.last_seen;
  return (
    <div className="p-3 bg-surface-input rounded-xl border border-divider">
      <div className="flex flex-wrap items-center gap-2">
        <SeverityBadge severity={event.severity} />
        <span className="text-sm font-semibold text-ink-main">{KIND_LABELS[event.kind] || event.kind}</span>
        {event.count > 1 && <span className="text-danger text-xs font-bold">×{event.count}</span>}
        <span className="text-xs font-mono text-ink-light break-all">{event.method} {event.endpoint}</span>
      </div>
      <div className="text-xs text-ink-light flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
        <span className="font-mono">{event.ip_address}</span>
        {event.user_id != null && <span>user #{event.user_id}</span>}
        <span title={sameTime ? undefined : `First seen ${formatInstant(event.first_seen)}`}>
          {formatRelative(event.last_seen)}
        </span>
        {event.user_agent && (
          // max-w-full on a phone: the old max-w-[16rem] was 256px inside a
          // 343px viewport, so it truncated far earlier than it needed to.
          <span className="truncate max-w-full sm:max-w-sm" title={event.user_agent}>{event.user_agent}</span>
        )}
      </div>
    </div>
  );
}

export function ErrorsTab({
  security, errors, securityError, errorsError, onRetrySecurity, onRetryErrors,
  view, onViewChange, severity, onSeverityChange, kind, onKindChange,
}) {
  const sev24 = {};
  (security?.summary?.bySeverity || []).forEach(r => { sev24[r.severity] = Number(r.total) || 0; });
  const kind24 = {};
  (security?.summary?.byKind || []).forEach(r => { kind24[r.kind] = Number(r.total) || 0; });
  const topIp = security?.summary?.topIps?.[0];

  return (
    <div className="space-y-4 sm:space-y-6">
      <SectionError message={securityError} onRetry={onRetrySecurity} />
      <SectionError message={errorsError} onRetry={onRetryErrors} />

      <StatGrid cols={4}>
        <StatCard title="Critical (24h)" value={sev24.critical || 0} subtitle="Security events" icon={Shield} tone="danger" />
        <StatCard title="High (24h)" value={sev24.high || 0} subtitle="Security events" icon={AlertTriangle} tone="warning" />
        <StatCard title="Failed logins (24h)" value={kind24.auth_failure || 0} subtitle="Across all IPs" icon={Users} tone="info" />
        <StatCard title="Busiest source (24h)" value={topIp ? Number(topIp.total) : 0} subtitle={topIp ? topIp.ip_address : 'No events'} icon={Globe} tone="accent" />
      </StatGrid>

      <Card
        title="Security events"
        icon={Shield}
        tone="danger"
        action={
          <div className="flex rounded-xl overflow-hidden border border-divider shrink-0">
            {['important', 'all'].map(v => (
              <button
                key={v}
                onClick={() => onViewChange(v)}
                aria-pressed={view === v}
                className={`px-3 py-2 text-sm font-medium ${
                  view === v ? 'bg-admin text-surface-card' : 'bg-surface-input text-ink-light hover:text-ink-main'
                }`}
              >
                {v === 'important' ? 'Important' : 'All'}
              </button>
            ))}
          </div>
        }
      >
        {view === 'all' && (
          <div className="flex flex-wrap gap-2 mb-3">
            <select
              value={severity}
              onChange={(e) => onSeverityChange(e.target.value)}
              aria-label="Filter by severity"
              className="px-3 min-h-[44px] text-sm rounded-xl border border-divider bg-surface-input text-ink-main"
            >
              <option value="all">All severities</option>
              {['critical', 'high', 'medium', 'low'].map(s => (
                <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
            <select
              value={kind}
              onChange={(e) => onKindChange(e.target.value)}
              aria-label="Filter by kind"
              className="px-3 min-h-[44px] text-sm rounded-xl border border-divider bg-surface-input text-ink-main"
            >
              <option value="all">All kinds</option>
              {Object.entries(KIND_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </div>
        )}
        <p className="text-xs text-ink-subtle mb-3 leading-snug">
          One row per source IP per endpoint. Repeats inside a window raise the ×count instead of
          adding rows, and floods are escalated automatically.
        </p>
        <ScrollRegion className="space-y-2" desktop="sm:max-h-[32rem]">
          {security?.events?.map(e => <SecurityEventRow key={e.id} event={e} />)}
          {security?.events?.length === 0 && (
            view === 'important'
              ? <EmptyState tone="success">No high-severity events. Quiet out there.</EmptyState>
              : <EmptyState>Nothing matches these filters</EmptyState>
          )}
        </ScrollRegion>
      </Card>

      <Card title="Server errors" icon={AlertTriangle} tone="danger">
        <ScrollRegion className="space-y-2">
          {errors?.errors?.map((e, i) => (
            <div key={i} className="p-3 bg-surface-input rounded-xl border border-danger-border">
              <div className="flex flex-wrap justify-between items-baseline gap-2">
                <span className="text-sm font-mono text-danger break-all">{e.method} {e.endpoint}</span>
                <span className="text-xs text-ink-light whitespace-nowrap" title={formatInstant(e.created_at)}>
                  {formatRelative(e.created_at)}
                </span>
              </div>
              <p className="text-sm text-ink-main mt-1 break-words">{e.error_message}</p>
              {e.stack_trace && (
                <details className="mt-1">
                  <summary className="text-xs text-ink-light cursor-pointer py-1">Stack trace</summary>
                  <pre className="text-[11px] text-ink-light mt-1 whitespace-pre-wrap break-all max-h-40 overflow-auto">{e.stack_trace}</pre>
                </details>
              )}
            </div>
          ))}
          {errors?.errors?.length === 0 && <EmptyState tone="success">No server errors logged</EmptyState>}
        </ScrollRegion>
      </Card>

      {errors?.errorCounts?.length > 0 && (
        <Card title="Errors by endpoint (24h)" icon={AlertTriangle} tone="warning">
          <RankedBars
            rows={errors.errorCounts.map(e => ({ label: e.endpoint, value: Number(e.count) }))}
            token={CHART_TOKENS[4]}
          />
        </Card>
      )}
    </div>
  );
}

// ── Custom range ────────────────────────────────────────────────────────────
export function RangeTab({ data, error, loading, onRetry, range, onRangeChange }) {
  return (
    <div className="space-y-4 sm:space-y-6">
      <SectionError message={error} onRetry={onRetry} />
      <Card title="Custom date range" icon={Calendar} tone="accent">
        {/* Stacks on a phone. color-scheme in index.css is what stops the
            native picker rendering light chrome over the dark theme. */}
        <div className="flex flex-col xs:flex-row gap-2 mb-4">
          <label className="flex-1">
            <span className="block text-xs text-ink-light mb-1">From</span>
            <input
              type="date"
              value={range.start}
              max={range.end}
              onChange={(e) => onRangeChange(e.target.value, range.end)}
              className="w-full px-3 min-h-[44px] text-sm rounded-xl border border-divider bg-surface-input text-ink-main outline-none focus:ring-2 focus:ring-admin"
            />
          </label>
          <label className="flex-1">
            <span className="block text-xs text-ink-light mb-1">To</span>
            <input
              type="date"
              value={range.end}
              min={range.start}
              onChange={(e) => onRangeChange(range.start, e.target.value)}
              className="w-full px-3 min-h-[44px] text-sm rounded-xl border border-divider bg-surface-input text-ink-main outline-none focus:ring-2 focus:ring-admin"
            />
          </label>
        </div>
        {loading && !data && <Loading />}
        {data && (
          <>
            <p className="text-xs text-ink-subtle mb-3">
              {formatCalendarDate(data.range?.start || range.start)} – {formatCalendarDate(data.range?.end || range.end)}
            </p>
            <StatGrid cols={4}>
              <StatCard title="New users" value={data.summary.newUsers} icon={UserPlus} tone="success" />
              <StatCard title="Active users" value={data.summary.activeUsers} icon={Users} tone="info" />
              <StatCard title="Completions" value={data.summary.completions} icon={CheckCircle} tone="accent" />
              <StatCard title="Habits created" value={data.summary.habitsCreated} icon={Target} tone="warning" />
            </StatGrid>
          </>
        )}
      </Card>

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <Card title="Daily active users" icon={Activity} tone="info">
            <BarChart
              data={data.dailyBreakdown.map(d => ({ date: d.date, count: d.active_users }))}
              token={CHART_TOKENS[1]}
              height={140}
            />
          </Card>
          <Card title="New signups" icon={UserPlus} tone="success">
            <BarChart data={data.signupsByDay} token={CHART_TOKENS[2]} height={140} />
          </Card>
        </div>
      )}
    </div>
  );
}
